import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye, Calendar, User } from 'lucide-react';
import api from '../utils/axios.ts';

const documentTypeColors = {
  application: 'bg-blue-100 text-blue-800',
  identity: 'bg-green-100 text-green-800',
  income_proof: 'bg-yellow-100 text-yellow-800',
  collateral: 'bg-purple-100 text-purple-800',
  guarantor: 'bg-indigo-100 text-indigo-800',
  other: 'bg-gray-100 text-gray-800',
};

const documentTypeIcons = {
  application: FileText,
  identity: User,
  income_proof: Calendar,
  collateral: FileText,
  guarantor: User,
  other: FileText,
};

export default function LoanDocumentViewer({ loan, onDownload }) {
  const [documents, setDocuments] = useState(loan?.loan_documents || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If loan_documents is not loaded, fetch them separately
    if (!loan?.loan_documents && loan?.id) {
      setLoading(true);
      api.get(`/memberloan/${loan.id}`)
        .then(response => {
          if (response.data?.data?.loan_documents) {
            setDocuments(response.data.data.loan_documents);
          }
        })
        .catch(error => {
          console.error('Failed to fetch loan documents:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setDocuments(loan?.loan_documents || []);
    }
  }, [loan]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <div className="text-center py-8">
          <FileText size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Documents Attached</h3>
          <p className="text-gray-500">
            This loan doesn't have any documents attached yet.
          </p>
        </div>
      </div>
    );
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const groupedDocuments = documents.reduce((acc, doc) => {
    const type = doc.document_type || 'other';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(doc);
    return acc;
  }, {});

  const getDocumentTypeName = (type) => {
    const names = {
      application: 'Loan Application Form',
      identity: 'Identity Document',
      income_proof: 'Income Proof',
      collateral: 'Collateral Documentation',
      guarantor: 'Guarantor Information',
      other: 'Other Documents',
    };
    return names[type] || 'Unknown';
  };

  return (
    <div className="bg-white rounded-lg border">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Loan Documents</h3>
        <p className="text-sm text-gray-500 mt-1">
          {documents.length} document{documents.length !== 1 ? 's' : ''} attached
        </p>
      </div>

      <div className="p-6">
        {/* Document Requirements */}
        {loan.requires_documents && loan.document_requirements && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Document Requirements</h4>
            <p className="text-blue-800 text-sm">{loan.document_requirements}</p>
          </div>
        )}

        {/* Required Document Categories */}
        {loan.document_categories && loan.document_categories.length > 0 && (
          <div className="mb-6">
            <h4 className="font-medium text-gray-900 mb-3">Required Document Types</h4>
            <div className="flex flex-wrap gap-2">
              {loan.document_categories.map((category) => (
                <span
                  key={category}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    documentTypeColors[category] || documentTypeColors.other
                  }`}
                >
                  {getDocumentTypeName(category)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Document Status */}
        {loan.requires_documents && (
          <div className="mb-6 p-4 border rounded-lg">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">Document Compliance</h4>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  loan.has_all_required_documents
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {loan.has_all_required_documents ? 'Complete' : 'Incomplete'}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {loan.has_all_required_documents
                ? 'All required documents have been submitted.'
                : 'Some required documents are still missing.'}
            </p>
          </div>
        )}

        {/* Grouped Documents */}
        <div className="space-y-6">
          {Object.entries(groupedDocuments).map(([type, documents]) => {
            const IconComponent = documentTypeIcons[type] || FileText;
            
            return (
              <div key={type} className="border border-gray-200 rounded-lg">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center">
                    <IconComponent size={18} className="mr-2 text-gray-600" />
                    <h5 className="font-medium text-gray-900">
                      {getDocumentTypeName(type)}
                    </h5>
                    <span className="ml-2 text-sm text-gray-500">
                      ({documents.length} document{documents.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                </div>

                <div className="divide-y divide-gray-200">
                  {documents.map((doc) => (
                    <div key={doc.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center mb-2">
                            <h6 className="text-sm font-medium text-gray-900 truncate">
                              {doc.title}
                            </h6>
                            {doc.is_required && (
                              <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                                Required
                              </span>
                            )}
                          </div>
                          
                          {doc.description && (
                            <p className="text-sm text-gray-600 mb-2">{doc.description}</p>
                          )}
                          
                          <div className="flex items-center text-xs text-gray-500 space-x-4">
                            <span>{doc.file_name}</span>
                            <span>{doc.formatted_file_size || formatFileSize(doc.file_size)}</span>
                            <span>{doc.file_type?.toUpperCase()}</span>
                            <span>Uploaded: {formatDate(doc.uploaded_at)}</span>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            onClick={() => window.open(doc.direct_url, '_blank')}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Document (Direct)"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => onDownload && onDownload(loan.id, doc.id, doc.file_name)}
                            className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                            title="Download (API)"
                          >
                            <Download size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
} 